import { useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { CAMERA_UNAVAILABLE_MESSAGE } from './consts'

export interface CapturedFile {
  file: Blob
  fileName: string
  contentType: string
}

export interface UseMediaCaptureParams {
  /** true 로 바뀌는 순간 녹화를 자동 시작한다(1.0 `VideoComponent` `autoRecord`, HEAD 조회 성공 시). */
  autoRecordTrigger: boolean
  /** 지정 시 그 초 후 자동 정지한다(1.0 `handleWebcam`). 0/미지정이면 수동 정지만. */
  recordSeconds?: number
  /**
   * 카메라 해상도(가로·세로 px). 주면 그 값을 목표(ideal)로 요청하고, 바뀌면 켜진 카메라에 다시 건다(1.0 GL09 `handleCamInfo.resolution`).
   * 0·미지정이면 브라우저 기본 해상도다(반품 화면).
   */
  resolution?: { width: number; height: number }
  /** 카메라를 못 켰을 때 `cameraError` 문구. 화면마다 다르면 넘긴다. */
  cameraUnavailableMessage?: string
}

export { CAMERA_UNAVAILABLE_MESSAGE } from './consts'

/** 해상도 제약 — 둘 다 양수일 때만 건다. 아니면 `true`(브라우저 기본). */
export const toVideoConstraints = (resolution?: { width: number; height: number }): MediaTrackConstraints | true =>
  resolution && resolution.width > 0 && resolution.height > 0 ? { width: { ideal: resolution.width }, height: { ideal: resolution.height } } : true

/** Safari(macOS·iOS)는 `video/webm` 을 지원하지 않아 `NotSupportedError` 를 던진다 — 지원 포맷을 순서대로 찾는다. */
const SUPPORTED_RECORDING_MIME_TYPES = ['video/webm;codecs=vp9', 'video/webm', 'video/mp4']

const getSupportedMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return ''
  return SUPPORTED_RECORDING_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? ''
}

/**
 * 웹캠 촬영·녹화 — `core.InspectionRecording` 은 시각 껍데기일 뿐 스트림·콜백이 없어(D11)
 * `<video>` + `MediaRecorder` 를 직접 붙인다. 2.0 은 `react-webcam` 이 없어(1.0 과 달리)
 * `getUserMedia` → `<video>` → `<canvas>` 캡처로 옮긴다(1.0 `VideoComponent.js` 로직 이식).
 *
 * 반품작업_신규 2.0(RGR18)·입고작업 2.0(GL17) 공용 — 화면 전용 문구는 인자로 받는다.
 */
export const useMediaCapture = ({ autoRecordTrigger, recordSeconds, resolution, cameraUnavailableMessage = CAMERA_UNAVAILABLE_MESSAGE }: UseMediaCaptureParams) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** 언마운트 뒤 늦게 도착한 getUserMedia 응답이 스트림을 새로 꽂지 못하게 막는다. */
  const cancelledRef = useRef(false)
  /** `stopRecording` 호출자가 `onstop`(Blob push) 완료까지 기다릴 수 있게 하는 대기열. */
  const stopWaitersRef = useRef<((file: CapturedFile | null) => void)[]>([])

  const [isRecording, setIsRecording] = useState(false)
  const [captures, setCaptures] = useState<CapturedFile[]>([])
  const [recordings, setRecordings] = useState<CapturedFile[]>([])
  const [cameraError, setCameraError] = useState<string | null>(null)
  /** 스트림을 받았는지 — 자동녹화 트리거가 카메라 준비 전에 한 번 지나가 첫 건을 놓치지 않게 호출부가 함께 본다(rgr06 사전 리뷰 S2 선례). */
  const [isCameraReady, setIsCameraReady] = useState(false)

  // 카메라를 켤 때 쓸 해상도 — 마운트 이펙트가 한 번만 돌아 최신 값을 ref 로 읽는다.
  const width = resolution?.width ?? 0
  const height = resolution?.height ?? 0
  const resolutionRef = useRef({ width, height })
  resolutionRef.current = { width, height }
  /** 실제 카메라에 건 해상도. 스트림이 오기 전에는 바꾸지 않는다 — 응답을 기다리는 사이 바뀐 값을 도착 뒤에 다시 걸기 위해서다. */
  const appliedResolutionRef = useRef({ width, height })

  /** 켜진 트랙에 해상도를 다시 건다. 트랙이 없으면 아무것도 하지 않고 false. 브라우저가 못 맞추면 가까운 값으로 둔다. */
  const applyResolution = useCallback((target: { width: number; height: number }): boolean => {
    const track = streamRef.current?.getVideoTracks?.()[0]
    if (!track) return false
    appliedResolutionRef.current = target
    const constraints = toVideoConstraints(target)
    if (constraints !== true && typeof track.applyConstraints === 'function') track.applyConstraints(constraints).catch(() => undefined)
    return true
  }, [])

  const startCamera = useCallback(async () => {
    // 미지원(비보안 출처·구형 브라우저)도 실패로 넘긴다 — 조용히 끝내면 '카메라 연결 중' 에서 멈춘다(LOGISPRD-14589).
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('getUserMedia unsupported')
    // 영상만 요청한다 — audio 를 함께 요청하면 마이크가 없거나 거부된 PC 에서 카메라까지 실패해 캡처가 조용히 멈춘다.
    // 1.0(react-webcam audio 기본 false)과 같다 — 녹화본에 소리가 없는 것도 1.0 과 같다(LOGISPRD-14549).
    const requested = resolutionRef.current
    const stream = await navigator.mediaDevices.getUserMedia({ video: toVideoConstraints(requested), audio: false })
    if (cancelledRef.current) {
      stream.getTracks().forEach((track) => track.stop())
      return
    }
    streamRef.current = stream
    appliedResolutionRef.current = requested
    // 권한 팝업 등으로 응답을 기다리는 사이 해상도가 바뀌었으면 도착한 트랙에 최신 값을 다시 건다
    const latest = resolutionRef.current
    if (latest.width !== requested.width || latest.height !== requested.height) applyResolution(latest)
    if (videoRef.current) videoRef.current.srcObject = stream
    setIsCameraReady(true)
  }, [applyResolution])

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  /** 캡처 파일명 규칙 — 1.0 `capture_YYYYMMDDHHmmss`. */
  const capture = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob((blob) => {
      if (!blob) return
      setCaptures((prev) => [...prev, { file: blob, fileName: `capture_${dayjs().format('YYYYMMDDHHmmss')}.jpg`, contentType: 'image/jpeg' }])
    }, 'image/jpeg')
  }, [])

  /** 녹화 파일명 규칙 — 1.0 `webcam_binary_YYYYMMDDHHmmss`(binary 첨부 모드). */
  const startRecording = useCallback(() => {
    // 녹화 중 판정은 실제 녹화기 상태로 — isRecording(화면 상태)은 정지 직후 아직 갱신 전일 수 있어, 저장 실패 뒤 곧바로 다시 켜는 호출이 묻혔다(리뷰 #884).
    if (!streamRef.current || mediaRecorderRef.current?.state === 'recording') return
    chunksRef.current = []
    const recorder = new MediaRecorder(streamRef.current, { mimeType: getSupportedMimeType() })
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorder.onstop = () => {
      const contentType = recorder.mimeType || 'video/webm'
      const blob = new Blob(chunksRef.current, { type: contentType })
      const file: CapturedFile = { file: blob, fileName: `webcam_binary_${dayjs().format('YYYYMMDDHHmmss')}.webm`, contentType }
      setRecordings((prev) => [...prev, file])
      setIsRecording(false)
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current)
        autoStopTimerRef.current = null
      }
      const waiters = stopWaitersRef.current
      stopWaitersRef.current = []
      waiters.forEach((resolve) => resolve(file))
    }
    mediaRecorderRef.current = recorder
    recorder.start()
    setIsRecording(true)

    if (recordSeconds && recordSeconds > 0) {
      autoStopTimerRef.current = setTimeout(() => recorder.state === 'recording' && recorder.stop(), recordSeconds * 1000)
    }
  }, [recordSeconds])

  /**
   * 정지 요청 — 실제 정지는 `onstop` 에서 Blob 을 push 한 뒤에 끝난다. 정지된 녹화본을 그대로
   * 돌려주므로(state 갱신을 기다릴 필요 없이) 저장 전 호출부가 곧바로 첨부 목록에 넣을 수 있다.
   * 녹화 중이 아니면 `null` 을 즉시 resolve 한다(자동정지 타이머로 이미 멈춘 뒤 호출된 경우 포함).
   */
  const stopRecording = useCallback((): Promise<CapturedFile | null> => {
    const recorder = mediaRecorderRef.current
    if (!recorder || recorder.state !== 'recording') return Promise.resolve(null)
    return new Promise((resolve) => {
      stopWaitersRef.current.push(resolve)
      recorder.stop()
    })
  }, [])

  /** 촬영 목록에서 한 장 지운다(검수 녹화 '파일' 목록의 x). */
  const removeCapture = useCallback((index: number) => {
    setCaptures((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const reset = useCallback(() => {
    setCaptures([])
    setRecordings([])
  }, [])

  /** 저장에 실어 보낸 촬영·녹화본만 지운다 — 저장이 도는 사이 찍은 다음 건 촬영본은 남긴다(LOGISPRD-14589). */
  const discard = useCallback((files: Blob[]) => {
    const sent = new Set(files)
    setCaptures((prev) => prev.filter((c) => !sent.has(c.file)))
    setRecordings((prev) => prev.filter((r) => !sent.has(r.file)))
  }, [])

  useEffect(() => {
    // 브라우저 오류 원문(영문) 대신 고정 문구 — rgr06 과 같다(LOGISPRD-14589).
    startCamera().catch(() => setCameraError(cameraUnavailableMessage))
    return () => {
      cancelledRef.current = true
      stopCamera()
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current)
        autoStopTimerRef.current = null
      }
      if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 해상도가 바뀌면 켜진 카메라에 다시 건다(첫 요청은 startCamera 가 실었다). 아직 스트림이 없으면 startCamera 가 도착 뒤에 맞춘다.
  useEffect(() => {
    const prev = appliedResolutionRef.current
    if (prev.width === width && prev.height === height) return
    applyResolution({ width, height })
  }, [width, height, applyResolution])

  // autoRecordTrigger 이펙트가 deps 억제 없이도 최신 startRecording(recordSeconds 반영)을 쓰도록 ref 로 잇는다.
  const startRecordingRef = useRef(startRecording)
  useEffect(() => {
    startRecordingRef.current = startRecording
  }, [startRecording])

  useEffect(() => {
    if (autoRecordTrigger) startRecordingRef.current()
  }, [autoRecordTrigger])

  return { videoRef, isRecording, isCameraReady, captures, recordings, capture, removeCapture, startRecording, stopRecording, reset, discard, cameraError }
}
